import { useTranslation } from 'react-i18next';
import { Badge } from '@mantine/core';
import { IconCoin } from '@tabler/icons-react';

interface PrepaymentBadgeProps {
  /**
   * Тип/процент предоплаты в любом виде: `'PERCENT_0'`/`'PERCENT_100'`, число (0/100) или числовая
   * строка. null/undefined/неизвестно → бейдж не рендерится (нечего показывать — ничего не показываем).
   */
  readonly value?: string | number | null;
  /**
   * Компактный вид (меньший размер) — для плотных списков (напр. дашборд «Лучшие предложения»).
   * Заливка/цвет те же, что у обычного (`filled` brand.6, белый текст) — читаемость важнее «тихости»;
   * отличается только размер. По умолчанию — крупный заметный вид рядом с «лучшей ценой» (превью, ориентир).
   */
  readonly compact?: boolean;
}

/** Нормализует тип предоплаты к проценту: 'PERCENT_100' → 100, 100 → 100, null/мусор → null. */
function toPercent(value: string | number | null | undefined): number | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const match = /(\d+)/.exec(value);
  return match ? Number(match[1]) : null;
}

// Заметный корпоративно-ЗЕЛЁНЫЙ бейдж «с предоплатой или без» — предоплата это важное измерение цены,
// не тихое метаданное. Стиль как у SectionLabel («Условия покупателя» в превью): заливка brand.6, белый
// жирный текст. Единый ЧИТАЕМЫЙ вид (filled + белый текст) везде — дашборд и геро-цена превью; compact
// меняет только размер. tt="none" — иначе Mantine Badge капсит текст, что запрещено Tone of Voice.
export default function PrepaymentBadge({ value, compact = false }: PrepaymentBadgeProps) {
  const { t } = useTranslation();
  const percent = toPercent(value);
  if (percent == null) {
    return null;
  }
  const label = percent === 0
    ? t('app.bestPrice.prepayment.none')
    : t('app.bestPrice.prepayment.percent', { percent });
  return (
    <Badge
      variant="filled"
      color="brand.6"
      size={compact ? 'sm' : 'lg'}
      radius="sm"
      tt="none"
      fw={600}
      leftSection={<IconCoin size={compact ? 13 : 15} />}
      styles={{
        label: {
          fontSize: compact ? 'var(--mantine-font-size-xs)' : 'var(--mantine-font-size-sm)',
          overflow: 'visible',
        },
      }}
      style={{ flexShrink: 0 }}
    >
      {label}
    </Badge>
  );
}
